
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      if (error) throw error;

      toast.success("Thank you for your message! We'll get back to you soon.");
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setFormError("Sorry, there was a problem sending your message. Please try again later.");
      toast.error("Sorry, there was a problem sending your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Functional Foodie</title>
        <meta 
          name="description" 
          content="Get in touch with Functional Foodie's expert dieticians in Johannesburg. We're here to help you achieve your health and nutrition goals." 
        />
        <link rel="canonical" href="https://functionalfoodie.co.za/contact" />
        <meta property="og:title" content="Contact Functional Foodie Dietitians | Johannesburg" />
        <meta property="og:description" content="Contact our registered dietitians in Glenhazel, Johannesburg. Call +27 78 351 5631 or email info@functionalfoodie.co.za." />
        <meta property="og:url" content="https://functionalfoodie.co.za/contact" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://functionalfoodie.co.za/#localbusiness",
          name: "Functional Foodie Registered Dietitians",
          image: "https://functionalfoodie.co.za/lovable-uploads/24972437-aa8b-4772-a032-7db8f669e478.png",
          url: "https://functionalfoodie.co.za/contact",
          telephone: "+27783515631",
          email: "info@functionalfoodie.co.za",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "48 Tanced Road, Glenhazel",
            addressLocality: "Johannesburg",
            addressRegion: "Gauteng",
            postalCode: "2192",
            addressCountry: "ZA",
          },
          areaServed: [
            { "@type": "City", name: "Johannesburg" },
            { "@type": "City", name: "Sandton" },
            { "@type": "Place", name: "Glenhazel" },
          ],
        })}</script>
      </Helmet>
      <div className="min-h-screen py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-primary mb-12">Contact Us</h1>
          
          {/* Contact Information */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-sm flex flex-col items-center text-center">
              <Mail className="w-8 h-8 text-primary mb-4" />
              <h2 className="text-xl font-semibold mb-2">Email Us</h2>
              <a 
                href="mailto:info@functionalfoodie.co.za" 
                className="text-gray-600 hover:text-primary transition-colors"
              >
                info@functionalfoodie.co.za
              </a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm flex flex-col items-center text-center">
              <Phone className="w-8 h-8 text-primary mb-4" />
              <h2 className="text-xl font-semibold mb-2">Call Us</h2>
              <a 
                href="tel:+27783515631" 
                className="text-gray-600 hover:text-primary transition-colors"
              >
                +27 78 351 5631
              </a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm flex flex-col items-center text-center">
              <MapPin className="w-8 h-8 text-primary mb-4" />
              <h2 className="text-xl font-semibold mb-2">Visit Us</h2>
              <a 
                href="https://www.google.com/maps/search/?api=1&query=48+Tanced+Road+Glenhazel+Johannesburg"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary transition-colors"
              >
                48 Tanced Road, Glenhazel, Johannesburg
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-sm">
            {formError && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone number
                </label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
